<template>
    <div class="module-chart-metrics">
        <template v-if="props.metricsData.length">
            <div class="metrics-table-inner">
                <generic-table
                    :filename="props.title || 'chart-metrics'"
                    no-data-found-message="No metrics data found"
                    small-header
                    cannot-edit
                    prevent-download
                    :data="tableFormattedMetrics"
                />
            </div>
            <div v-if="!props.calculatingMetrics" class="metrics-toolbar">
                <strong class="metrics-label">Metrics calculated:</strong>
                <span v-if="props.metricCalcData" class="metrics-date">{{ props.metricCalcData }}</span>
                <action-button
                    title="Recalculate metrics"
                    :icon="['fas', 'arrow-rotate-left']"
                    @click="handleRecalculate"
                />
                <action-button
                    title="Download metrics"
                    :icon="['fas', 'download']"
                    :is-disabled="!props.metricsData.length"
                    @click="downloadMetrics"
                />
            </div>
        </template>
        <div v-else>
            <p>No metrics data found</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { downloadChartMetrics } from '@/utils/excelUtils.ts';
import GenericTable from '@/components/powerMarkets/tables/GenericTable.vue';
import { IModuleChartMetrics } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { buildGenericTableData } from '@/helpers/table/genericTableData.ts';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';

const METRICS_HEADERS: Array<{label: string; key: keyof IModuleChartMetrics}> = [
    { label: 'Series', key: 'id' },
    { label: 'Max', key: 'max' },
    { label: 'Min', key: 'min' },
    { label: 'Average', key: 'average' },
    { label: 'Sum', key: 'sum' },
    { label: 'SD', key: 'standardDeviation' },
    { label: 'Var', key: 'variance' }];

const userSettingsStore = useUserSettingsStore();

const props = defineProps<{
    metricsData: Array<IModuleChartMetrics>;
    title: string | undefined;
    metricCalcData: string | null;
    calculatingMetrics: boolean;
}>();

const emit = defineEmits<{
    recalculate: [];
}>();

function handleRecalculate (): void {
    emit('recalculate');
}

const tableFormattedMetrics = computed<IGenericTableData>(() => {
    const table = buildGenericTableData(
        METRICS_HEADERS.map(header => ({
            id: header.key,
            title: header.label,
            isPlant: header.key === 'id',
            firstRowHasBeenSet: false,
        })),
        props.metricsData.map(item => METRICS_HEADERS.map(header => {
            const value = item[header.key];
            const isNumeric = header.key !== 'id' && !Number.isNaN(Number(value));
            return isNumeric ? Number(value) : value;
        })),
    );
    const data = table.data.map((row, index) => ({
        ...row,
        type: 'metric',
        uniqueId: `metric-${index}`,
        hideRow: false,
    }));

    return {
        ascending: false,
        columnsToHide: [],
        currentFiltersByHeading: null,
        data,
        extraData: null,
        headerFirstRowById: table.headerFirstRowById,
        headerSecondRow: [],
        headerToSort: null,
        numberOfRowsForSerialisation: data.length,
        tabName: 'Chart Metrics',
        type: 'metrics',
    };
});

function downloadMetrics (): void {
    if (!props.metricsData) return;
    downloadChartMetrics(
        props.metricsData,
        METRICS_HEADERS,
        userSettingsStore.userSettings.otherSettings.data.excelAsCsv ? 'csv' : 'xls',
        props.title ?? 'chart-metrics',
    );
}

</script>

<style lang="scss" scoped>

.module-chart-metrics {
    padding-left: var(--largeSpacing);
    display: grid;
    grid-template-rows: 1fr auto;
    gap: var(--defaultSpacing);
    height: 100%;
    overflow: hidden;
}

.metrics-table-inner {
    height: 100%;
    overflow: hidden;
}

.metrics-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--smallSpacing);
    margin-bottom: var(--defaultSpacing);
    font-size: var(--smallFontSize);
    color: var(--neutral900);
}

.metrics-label {
    font-weight: 600;
}

.metrics-date {
    white-space: nowrap;
}

</style>
